// Ported from dev-scrips-pm-react src/scheduling/store/scheduling-store.ts (PROD-1190 calendar port)
/**
 * Scheduling UI Store — Zustand
 *
 * Owns ONLY client UI state. Server state (appointments, providers, slots)
 * is owned by React Query hooks in @/scheduling/services/*.
 *
 * Rule: Never put server data in this store. Never put UI state in React Query.
 *
 * Persistence: `selectedSpecialties` and `selectedProviderIds` survive page
 * reloads under a per-user localStorage key (mirrors Flutter's
 * `selected_specialities + userId` / `selected_providers + userId`
 * SharedPreferences keys). When the auth user changes (login/logout),
 * the store rehydrates from the new user's key automatically — so user A's
 * selections never leak into user B's session.
 *
 * `selectedDate`, `activeView`, and panel state are NOT persisted —
 * they reset to defaults on each load (matches Flutter).
 */
import { create } from 'zustand';
import {
  persist,
  type StateStorage,
} from 'zustand/middleware';
import { useAuthStore } from '@/auth/auth-store';
import { practiceNow } from '@/shared/lib/practice-time';
import { ymdLocal } from '@/shared/lib/ymd-local';

export type CalendarView = 'Day' | 'Week' | 'Month' | 'ByRoom' | 'ByDoctor';
export type PanelMode = 'create' | 'view' | 'edit' | 'block' | null;

interface SchedulingState {
  selectedDate: string;
  activeView: CalendarView;
  panelMode: PanelMode;
  selectedAppointmentId: string | null;
  selectedProviderIds: string[];
  /** Multi-select; empty list = "All" (no filter). */
  selectedSpecialties: string[];
  /**
   * Appointment status codes the user wants to see. Empty = no explicit
   * filter; the helper still hides Cancelled rows by default (toggle the
   * Cancelled pill to surface them). Non-empty = restrict to those codes.
   */
  selectedStatusCodes: string[];
  /**
   * Invoice statuses ("Draft" / "Issued" / "Balanced") — applied
   * client-side to narrow the appointments response. Empty = no filter.
   */
  selectedInvoiceStatuses: string[];
  /**
   * Practice location ids the user has toggled. Empty = no filter
   * (every location passes — matches Flutter where the buttons all
   * render gray on first load).
   */
  selectedLocationIds: string[];
  /**
   * The calendar's selected practice. Persisted (user-scoped) so the choice
   * survives navigating away and back — Flutter keeps the selected practice
   * across pages (parent-owned `selectedPractice`); PA persists it here.
   * `null` = not yet chosen → the page falls back to primary / user.practiceId.
   */
  selectedPracticeId: string | null;

  setSelectedDate: (date: string) => void;
  setActiveView: (view: CalendarView) => void;
  openPanel: (mode: PanelMode, appointmentId?: string | null) => void;
  closePanel: () => void;
  setSelectedProviders: (ids: string[]) => void;
  toggleProvider: (id: string) => void;
  clearProviders: () => void;

  setSelectedSpecialties: (s: string[]) => void;
  toggleSpecialty: (s: string) => void;
  clearSpecialties: () => void;

  setSelectedStatusCodes: (codes: string[]) => void;
  toggleStatusCode: (code: string) => void;
  clearStatusCodes: () => void;

  setSelectedInvoiceStatuses: (codes: string[]) => void;
  toggleInvoiceStatus: (code: string) => void;
  clearInvoiceStatuses: () => void;

  toggleLocation: (id: string) => void;
  clearLocations: () => void;

  setSelectedPracticeId: (id: string | null) => void;
}

const ANON_USER_ID = 'anonymous';

/** Resolve the storage suffix from the currently-known auth user. */
function currentUserKeySuffix(): string {
  return useAuthStore.getState().user?.userId ?? ANON_USER_ID;
}

/**
 * localStorage adapter that prefixes the persist `name` with the current
 * user's id, so user A and user B never share scheduling preferences on
 * the same browser. Mirrors Flutter's `key + userId` convention.
 */
const userScopedStorage: StateStorage = {
  getItem: (name) => {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem(`${name}.${currentUserKeySuffix()}`);
  },
  setItem: (name, value) => {
    if (typeof window === 'undefined') return;
    localStorage.setItem(`${name}.${currentUserKeySuffix()}`, value);
  },
  removeItem: (name) => {
    if (typeof window === 'undefined') return;
    localStorage.removeItem(`${name}.${currentUserKeySuffix()}`);
  },
};

export const useSchedulingStore = create<SchedulingState>()(
  persist(
    (set, get) => ({
      selectedDate: ymdLocal(practiceNow()),
      activeView: 'Day',
      panelMode: null,
      selectedAppointmentId: null,
      selectedProviderIds: [],
      selectedSpecialties: [],
      // Empty by default — the day-view shows every status except Cancelled,
      // which the filter helper hides on its own. Toggling a pill or picking
      // codes in the Statuses dropdown narrows to the explicit set.
      selectedStatusCodes: [],
      selectedInvoiceStatuses: [],
      selectedLocationIds: [],
      selectedPracticeId: null,

      setSelectedDate: (date) => set({ selectedDate: date }),
      setActiveView: (view) => set({ activeView: view }),
      openPanel: (mode, appointmentId = null) =>
        set({ panelMode: mode, selectedAppointmentId: appointmentId }),
      closePanel: () => set({ panelMode: null, selectedAppointmentId: null }),

      setSelectedProviders: (ids) => set({ selectedProviderIds: ids }),
      toggleProvider: (id) => {
        const current = get().selectedProviderIds;
        set({
          selectedProviderIds: current.includes(id)
            ? current.filter((x) => x !== id)
            : [...current, id],
        });
      },
      clearProviders: () => set({ selectedProviderIds: [] }),

      setSelectedSpecialties: (s) => set({ selectedSpecialties: s }),
      toggleSpecialty: (s) => {
        const current = get().selectedSpecialties;
        set({
          selectedSpecialties: current.includes(s)
            ? current.filter((x) => x !== s)
            : [...current, s],
        });
      },
      clearSpecialties: () => set({ selectedSpecialties: [] }),

      setSelectedStatusCodes: (codes) => set({ selectedStatusCodes: codes }),
      toggleStatusCode: (code) => {
        const current = get().selectedStatusCodes;
        set({
          selectedStatusCodes: current.includes(code)
            ? current.filter((x) => x !== code)
            : [...current, code],
        });
      },
      clearStatusCodes: () => set({ selectedStatusCodes: [] }),

      setSelectedInvoiceStatuses: (codes) =>
        set({ selectedInvoiceStatuses: codes }),
      toggleInvoiceStatus: (code) => {
        const current = get().selectedInvoiceStatuses;
        set({
          selectedInvoiceStatuses: current.includes(code)
            ? current.filter((x) => x !== code)
            : [...current, code],
        });
      },
      clearInvoiceStatuses: () => set({ selectedInvoiceStatuses: [] }),

      toggleLocation: (id) => {
        const current = get().selectedLocationIds;
        set({
          selectedLocationIds: current.includes(id)
            ? current.filter((x) => x !== id)
            : [...current, id],
        });
      },
      clearLocations: () => set({ selectedLocationIds: [] }),

      setSelectedPracticeId: (id) => set({ selectedPracticeId: id }),
    }),
    {
      name: 'scrips.scheduling',
      storage: {
        getItem: (name) => {
          // userScopedStorage is synchronous (backed by localStorage)
          // so the StateStorage `string | Promise<string | null>`
          // return narrows to string at runtime — cast.
          const raw = userScopedStorage.getItem(name) as string | null;
          return raw ? JSON.parse(raw) : null;
        },
        setItem: (name, value) =>
          userScopedStorage.setItem(name, JSON.stringify(value)),
        removeItem: (name) => userScopedStorage.removeItem(name),
      },
      // `partialize` typed as full-state by Zustand, but the API is
      // specifically designed to return a SUBSET — cast.
      partialize: ((state: SchedulingState) => ({
        selectedSpecialties: state.selectedSpecialties,
        selectedProviderIds: state.selectedProviderIds,
        selectedStatusCodes: state.selectedStatusCodes,
        selectedInvoiceStatuses: state.selectedInvoiceStatuses,
        selectedLocationIds: state.selectedLocationIds,
        selectedPracticeId: state.selectedPracticeId,
      })) as (state: SchedulingState) => SchedulingState,
    },
  ),
);

// ---------------------------------------------------------------------------
// Auth-aware rehydration
// ---------------------------------------------------------------------------
// When the auth user changes (login / logout / refresh-induced switch), the
// localStorage key suffix changes too. Manually rehydrate so the in-memory
// state reflects the new user's persisted selections immediately, and reset
// to empty if the new user has none.
if (typeof window !== 'undefined') {
  let lastUserId: string | undefined = useAuthStore.getState().user?.userId;
  useAuthStore.subscribe((state) => {
    const newUserId = state.user?.userId;
    if (newUserId === lastUserId) return;
    lastUserId = newUserId;
    // Reset to defaults first (so a logout + new login doesn't briefly show
    // the previous user's selections), then rehydrate from the new key.
    useSchedulingStore.setState({
      selectedSpecialties: [],
      selectedProviderIds: [],
      selectedStatusCodes: [],
      selectedInvoiceStatuses: [],
      selectedLocationIds: [],
      selectedPracticeId: null,
    });
    void useSchedulingStore.persist.rehydrate();
  });
}
